import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtDate, fmtQty } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Gauge, History, Trash2 } from "lucide-react";

const EMPTY_METER = { code: "", equipment_id: "", meter_type: "hours", uom: "h", description: "", active: true };

export default function MntMeters() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_METER);
  const [readingOpen, setReadingOpen] = useState(false);
  const [readingMeter, setReadingMeter] = useState(null);
  const [reading, setReading] = useState({ reading: 0, notes: "" });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [readings, setReadings] = useState([]);
  const editable = can("admin", "maintenance", "warehouse");

  const removeMeter = async (m) => {
    if (!window.confirm(`${t("mnt.m.deleteConfirm")} ${m.code} (${m.equipment_code})?`)) return;
    try { await api.delete(`/mnt/meters/${m.id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeReading = async (r) => {
    if (!window.confirm(`${t("mnt.m.deleteReadingConfirm")} ${fmtQty(r.reading)}?`)) return;
    try {
      await api.delete(`/mnt/meters/${r.meter_id}/readings/${r.id}`);
      toast.success(t("common.deleted"));
      setReadings((prev) => prev.filter((x) => x.id !== r.id));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => api.get("/mnt/meters").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data)); }, []);

  const save = async () => {
    try { await api.post("/mnt/meters", form); toast.success(t("common.saved")); setOpen(false); setForm(EMPTY_METER); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openReading = (m) => {
    setReadingMeter(m);
    setReading({ reading: m.last_reading || 0, notes: "" });
    setReadingOpen(true);
  };

  const saveReading = async () => {
    try {
      await api.post(`/mnt/meters/${readingMeter.id}/reading`, {
        reading: parseFloat(reading.reading) || 0, notes: reading.notes,
      });
      toast.success(t("mnt.m.readingSaved"));
      setReadingOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openHistory = async (m) => {
    setReadingMeter(m);
    const r = await api.get(`/mnt/meters/${m.id}/readings`);
    setReadings(r.data);
    setHistoryOpen(true);
  };

  return (
    <div data-testid="mnt-meters-page">
      <PageHeader title={t("mnt.m.title")} subtitle={t("mnt.m.subtitle")}>
        {editable && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY_METER); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="meter-new-button"><Plus size={13} className="mr-1" /> {t("mnt.m.new")}</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader><DialogTitle className="uppercase font-black">{t("mnt.m.new")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.m.code")}</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none h-8 text-xs font-black tabular-nums" data-testid="meter-form-code" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.m.type")}</Label>
                  <Select value={form.meter_type} onValueChange={(v) => { setForm({ ...form, meter_type: v, uom: ({hours:"h",km:"km",cycles:"ciclos",pressure:"bar",temp:"°C",custom:""})[v] || "" }); }}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="meter-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{["hours","km","cycles","pressure","temp","custom"].map((tp) => <SelectItem key={tp} value={tp}>{t(`mnt.m.type.${tp}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.equipment")}</Label>
                  <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="meter-form-equipment"><SelectValue placeholder={t("mnt.selectEquipment")} /></SelectTrigger>
                    <SelectContent>{equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.m.uom")}</Label><Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} className="rounded-none h-8 text-xs" data-testid="meter-form-uom" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none h-8 text-xs" data-testid="meter-form-description" /></div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.code || !form.equipment_id} className="rounded-none text-xs font-bold" data-testid="meter-save-button">{t("common.save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="meter-table">
          <table className="tbl">
            <thead><tr><th>{t("mnt.m.code")}</th><th>{t("mnt.equipment")}</th><th>{t("mnt.m.type")}</th><th>{t("mnt.m.lastReading")}</th><th>{t("mnt.m.uom")}</th><th>{t("mnt.m.lastReadingAt")}</th><th></th></tr></thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} data-testid={`meter-row-${m.code}`}>
                  <td className="font-bold tabular-nums">{m.code}</td>
                  <td className="text-[11px]"><span className="font-bold">{m.equipment_code}</span> · {m.equipment_name}</td>
                  <td className="text-[10px] font-bold">{t(`mnt.m.type.${m.meter_type}`)}</td>
                  <td className="tabular-nums font-bold">{fmtQty(m.last_reading)}</td>
                  <td className="text-[11px]">{m.uom}</td>
                  <td className="text-[10px] text-muted-foreground">{m.last_reading_at ? fmtDate(m.last_reading_at) : "—"}</td>
                  <td>
                    <div className="flex gap-2">
                      {editable && <button onClick={() => openReading(m)} className="text-emerald-600 hover:opacity-70" title={t("mnt.m.addReading")} data-testid={`meter-add-reading-${m.code}`}><Gauge size={13} /></button>}
                      <button onClick={() => openHistory(m)} className="text-primary hover:opacity-70" title={t("mnt.m.readings")} data-testid={`meter-history-${m.code}`}><History size={13} /></button>
                      {can("admin") && <button onClick={() => removeMeter(m)} className="text-muted-foreground hover:text-destructive" title={t("common.delete")} data-testid={`meter-delete-${m.code}`}><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={readingOpen} onOpenChange={setReadingOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.m.addReading")}</DialogTitle></DialogHeader>
          {readingMeter && (
            <div className="text-[11px] text-muted-foreground mb-2">
              <span className="font-bold">{readingMeter.code}</span> · {readingMeter.equipment_code} · Última: {fmtQty(readingMeter.last_reading)} {readingMeter.uom}
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.m.reading")}</Label><Input type="number" step="0.01" value={reading.reading} onChange={(e) => setReading({ ...reading, reading: e.target.value })} className="rounded-none h-8 text-xs" data-testid="reading-input" /></div>
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("common.notes")}</Label><Input value={reading.notes} onChange={(e) => setReading({ ...reading, notes: e.target.value })} className="rounded-none h-8 text-xs" data-testid="reading-notes" /></div>
          </div>
          <DialogFooter><Button onClick={saveReading} className="rounded-none text-xs font-bold" data-testid="reading-save-button">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("mnt.m.readings")}</DialogTitle></DialogHeader>
          {readings.length === 0 ? <div className="text-center text-[11px] text-muted-foreground py-6">—</div> : (
            <table className="tbl">
              <thead><tr><th>{t("common.date") || "Fecha"}</th><th>{t("mnt.m.reading")}</th><th>{t("common.notes")}</th><th></th></tr></thead>
              <tbody>
                {readings.map((r) => (
                  <tr key={r.id} data-testid={`reading-row-${r.id}`}>
                    <td className="text-[11px] tabular-nums">{fmtDate(r.reading_at)}</td>
                    <td className="text-[11px] tabular-nums font-bold">{fmtQty(r.reading)}</td>
                    <td className="text-[10px] text-muted-foreground">{r.notes || "—"}</td>
                    <td>{editable && <button onClick={() => removeReading(r)} className="text-muted-foreground hover:text-destructive" data-testid={`reading-delete-${r.id}`}><Trash2 size={12} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
